import {TruncatedText} from '@app/lui2/text';
import Luiv3Constants from '@app/lui3/constants';
import {useCallback} from 'react';
import styled from 'styled-components';

import Avatar from '../../Avatar';
import {
  fetchOutlookPersonAutocomplete,
  OutlookPersonFromEmailJson,
} from '../../requests';
import {usePodRequest} from '../../usePodRequest';
import EntityContainer from './EntityContainer';
import EntityLink from './EntityLink';
import LinkSelector from './LinkSelector';
import {MessageAttachment, PersonLink} from './structs';

interface Props {
  agencyId: number;
  recipientEmails: readonly string[];
  personLinks: PersonLink[];
  setPersonLinks: (personLinks: PersonLink[]) => void;
  attachments: MessageAttachment[];
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const OptionName = styled(TruncatedText)`
  min-width: 0;
`;

const OptionId = styled.span`
  flex-shrink: 0;
  color: ${Luiv3Constants.baseColor['gray-600']};
`;

const PersonLinks = ({
  agencyId,
  recipientEmails,
  personLinks,
  setPersonLinks,
  attachments,
}: Props) => {
  const {request} = usePodRequest();

  const linkedPersonIds = new Set(personLinks.map((link) => link.person.id));

  const searchPeople = useCallback(
    async (query: string) => {
      const {people} = await fetchOutlookPersonAutocomplete(request, agencyId, {
        query,
        recipientEmails,
      });
      return people;
    },
    [agencyId, recipientEmails, request]
  );

  const handleSelect = (person: OutlookPersonFromEmailJson) => {
    if (linkedPersonIds.has(person.id)) {
      return;
    }
    setPersonLinks([
      ...personLinks,
      {person, pinned: false, attachmentIds: []},
    ]);
  };

  const handleTogglePinned = (personId: number) => {
    setPersonLinks(
      personLinks.map((link) =>
        link.person.id === personId ? {...link, pinned: !link.pinned} : link
      )
    );
  };

  const handleRemove = (personId: number) => {
    setPersonLinks(personLinks.filter((link) => link.person.id !== personId));
  };

  const handleAttachmentsChanged = (
    personId: number,
    attachmentIds: string[]
  ) => {
    setPersonLinks(
      personLinks.map((link) =>
        link.person.id === personId ? {...link, attachmentIds} : link
      )
    );
  };

  return (
    <Container>
      <LinkSelector
        title="People"
        placeholder="Add People..."
        searchOptions={searchPeople}
        getOptionKey={(person) => person.id}
        renderOption={(person) => (
          <>
            <OptionName>{person.name ?? ''}</OptionName>
            <OptionId>#{person.id}</OptionId>
          </>
        )}
        onSelect={handleSelect}
        excludeKeys={linkedPersonIds}
      />
      {personLinks.length > 0 && (
        <EntityContainer>
          {personLinks.map((link) => (
            <EntityLink
              avatar={
                <Avatar
                  name={link.person.name ?? ''}
                  imageUrl={link.person.profile_picture_thumb_url}
                  size="sm"
                />
              }
              key={link.person.id}
              name={link.person.name ?? ''}
              subtitle={link.person.emails
                .map((email) => email.value)
                .join(', ')}
              attachmentIds={link.attachmentIds}
              allAttachments={attachments}
              showPinned
              pinned={link.pinned}
              attachmentType="Resume"
              togglePinned={() => handleTogglePinned(link.person.id)}
              remove={() => handleRemove(link.person.id)}
              onAttachmentsChanged={(attachmentIds: string[]) =>
                handleAttachmentsChanged(link.person.id, attachmentIds)
              }
            />
          ))}
        </EntityContainer>
      )}
    </Container>
  );
};

export default PersonLinks;
